import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { useAutoDismissNotice } from '../../hooks/useAutoDismissNotice';
import {
  toggleDxfLayerVisible,
  setAllDxfLayersVisible,
  toggleEntityTypeVisible,
  setAllEntityTypesInLayerVisible,
  setFocusedPolyline,
  setHoveredLayer,
  markDxfLayerVisibilitySaved,
  discardDxfLayerVisibilityChanges,
  setFloorPlanOpacity,
} from '../../features/floorplan/floorplanSlice';
import { setDxfLayersPanelOpen } from '../../features/ui/uiSlice';
import { aciToHex } from '../../utils/dxf';
import {
  saveFloorPlanLayerVisibility,
} from '../../utils/dxfApi';
import { VisibilityIcon } from '../icons/ContextMenuIcons';

// ─── Types ────────────────────────────────────────────────────────────────────

interface EntityTypeGroup {
  type: string;
  /** Indices of polylines of this type in this layer */
  indices: number[];
  visible: boolean;
}

interface LayerViewModel {
  layerIndex: number;
  name: string;
  color: number;
  category?: string;
  visible: boolean;
  frozen?: boolean;
  entityGroups: EntityTypeGroup[];
  /** Whether this layer matches the current search */
  matchesSearch: boolean;
  /** Entity type groups that match the search (subset of entityGroups) */
  matchingGroups: EntityTypeGroup[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_VISIBLE_ENTITIES = 150;
const ENTITY_ICON: Record<string, string> = {
  LINE: '╱',
  LWPOLYLINE: '⌒',
  POLYLINE: '⌒',
  ARC: '◔',
  CIRCLE: '○',
  ELLIPSE: '⬭',
  SPLINE: '〜',
  TEXT: 'T',
  MTEXT: 'T',
  HATCH: '▨',
  DIMENSION: '↔',
  INSERT: '⊞',
};

const DEFAULT_STROKE = '#475569';

// ─── Main Component ───────────────────────────────────────────────────────────

const DxfLayersPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const layers = useAppSelector((s) => s.floorplan.layers);
  const floorPlanId = useAppSelector((s) => s.floorplan.floorPlanId);
  const floorplanName = useAppSelector((s) => s.floorplan.name);
  const polylineLayers = useAppSelector((s) => s.floorplan.polylineLayers);
  const polylineMeta = useAppSelector((s) => s.floorplan.polylineMeta);
  const hiddenEntityTypes = useAppSelector((s) => s.floorplan.hiddenEntityTypes);
  const focusedPolylineIndex = useAppSelector((s) => s.floorplan.focusedPolylineIndex);
  const dirtyLayerIndices = useAppSelector((s) => s.floorplan.dirtyLayerIndices);
  const floorPlanOpacity = useAppSelector((s) => s.floorplan.opacity);

  const [query, setQuery] = useState('');
  const [expandedLayers, setExpandedLayers] = useState<Set<number>>(new Set());
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set());
  const [isSavingVisibility, setIsSavingVisibility] = useState(false);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);
  const clearSaveNotice = useCallback(() => setSaveNotice(null), []);
  useAutoDismissNotice(saveNotice, clearSaveNotice);

  // Ref map from "layerIndex" → DOM element so we can scroll into view on focus.
  const layerRowRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const focusedEntityRef = useRef<HTMLDivElement | null>(null);

  // ─── Build grouped view model ──────────────────────────────────────────────

  const layerViewModels = useMemo<LayerViewModel[]>(() => {
    // For each layer, group polylines by entity type.
    const groupsByLayer: Map<number, Map<string, number[]>> = new Map();
    for (let li = 0; li < layers.length; li++) groupsByLayer.set(li, new Map());

    for (let pi = 0; pi < polylineLayers.length; pi++) {
      const li = polylineLayers[pi];
      const type = polylineMeta[pi]?.type ?? 'UNKNOWN';
      const layerMap = groupsByLayer.get(li);
      if (!layerMap) continue;
      if (!layerMap.has(type)) layerMap.set(type, []);
      layerMap.get(type)!.push(pi);
    }

    const q = query.trim().toLowerCase();

    return layers.map((layer, li) => {
      const layerMap = groupsByLayer.get(li) ?? new Map<string, number[]>();

      const entityGroups: EntityTypeGroup[] = Array.from(layerMap.entries()).map(
        ([type, indices]) => ({
          type,
          indices,
          visible: !hiddenEntityTypes[`${li}:${type}`],
        })
      );
      entityGroups.sort((a, b) => b.indices.length - a.indices.length);

      const layerNameMatch = !q || layer.name.toLowerCase().includes(q);
      const matchingGroups = q
        ? entityGroups.filter(
            (g) => layerNameMatch || g.type.toLowerCase().includes(q)
          )
        : entityGroups;

      const matchesSearch = !q || layerNameMatch || matchingGroups.length > 0;

      return {
        layerIndex: li,
        name: layer.name,
        color: layer.color,
        category: layer.category,
        visible: layer.visible,
        frozen: layer.frozen,
        entityGroups,
        matchesSearch,
        matchingGroups: q ? matchingGroups : entityGroups,
      };
    });
  }, [layers, polylineLayers, polylineMeta, hiddenEntityTypes, query]);

  const filteredLayers = useMemo(
    () => layerViewModels.filter((l) => l.matchesSearch),
    [layerViewModels]
  );

  const visibleLayerCount = layers.filter((l) => l.visible).length;
  const dirtyCount = dirtyLayerIndices.length;
  const canSaveVisibility = dirtyCount > 0 && !isSavingVisibility;
  const hasFocusedSelection = focusedPolylineIndex != null;
  const canClearSelection = dirtyCount > 0 || hasFocusedSelection;

  const handleClearSelection = useCallback(() => {
    dispatch(discardDxfLayerVisibilityChanges());
    setExpandedLayers(new Set());
    setExpandedTypes(new Set());
    setSaveNotice(
      dirtyCount > 0
        ? 'Cleared selection and reverted unsaved visibility changes.'
        : 'Cleared layer selection.'
    );
  }, [dirtyCount, dispatch]);


  const handleSaveVisibility = useCallback(async () => {
    if (!floorPlanId) {
      setSaveNotice('Cannot save: this floor plan has no backend id. Open it from the project list.');
      return;
    }
    if (dirtyLayerIndices.length === 0) {
      setSaveNotice('No visibility changes to save.');
      return;
    }

    // Endpoint is "show-layers-only": send the full whitelist of currently visible names.
    const layerNames = layers
      .filter((layer) => layer.visible !== false)
      .map((layer) => layer.name)
      .filter((name) => typeof name === 'string' && name.trim().length > 0);

    setIsSavingVisibility(true);
    setSaveNotice(null);
    try {
      await saveFloorPlanLayerVisibility({ floorPlanId, layerNames });
      dispatch(markDxfLayerVisibilitySaved());
      setSaveNotice(
        layerNames.length
          ? `Saved ${layerNames.length} visible layer(s).`
          : 'Saved: all layers hidden.'
      );
    } catch (err) {
      console.error('Failed to save layer visibility:', err);
      setSaveNotice(err instanceof Error ? err.message : 'Failed to save layer visibility.');
    } finally {
      setIsSavingVisibility(false);
    }
  }, [dispatch, dirtyLayerIndices.length, floorPlanId, layers]);

  // ─── Auto-expand & scroll when focusedPolylineIndex changes ───────────────

  useEffect(() => {
    if (focusedPolylineIndex == null) return;
    const li = polylineLayers[focusedPolylineIndex];
    if (li == null) return;
    const type = polylineMeta[focusedPolylineIndex]?.type ?? 'UNKNOWN';

    // Expand the layer and the entity type group.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setExpandedLayers((prev) => new Set(prev).add(li));
    setExpandedTypes((prev) => new Set(prev).add(`${li}:${type}`));

    // Scroll the layer header row into view after the next paint.
    requestAnimationFrame(() => {
      layerRowRefs.current[li]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      setTimeout(() => {
        focusedEntityRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }, 120);
    });
  }, [focusedPolylineIndex, polylineLayers, polylineMeta]);
  
  // Clear hovered layer index on panel unmount
  useEffect(() => {
    return () => {
      dispatch(setHoveredLayer(null));
    };
  }, [dispatch]);

  // ─── Helpers ───────────────────────────────────────────────────────────────

  const toggleLayerExpand = useCallback((li: number) => {
    setExpandedLayers((prev) => {
      const next = new Set(prev);
      if (next.has(li)) next.delete(li);
      else next.add(li);
      return next;
    });
  }, []);

  const toggleTypeExpand = useCallback((key: string) => {
    setExpandedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // ─── Empty state ───────────────────────────────────────────────────────────

  if (!floorplanName || layers.length === 0) {
    return (
      <div className="h-full flex flex-col text-sm">
        <PanelHeader onClose={() => dispatch(setDxfLayersPanelOpen(false))} />
        <div className="flex-1 flex items-center justify-center p-6 text-center">
          <p className="text-xs text-gray-400">
            No DXF imported yet. Use{' '}
            <span className="font-mono">🧩 Import DXF JSON</span> to load layers here.
          </p>
        </div>
      </div>
    );
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="h-full flex flex-col text-sm select-none">
      <PanelHeader onClose={() => dispatch(setDxfLayersPanelOpen(false))} />

      {/* File name + summary */}
      <div className="px-3 py-2 border-b border-gray-200 shrink-0 space-y-2">
        <div className="flex items-center gap-1.5">
          <span className="text-gray-400 text-[11px]">🗺</span>
          <span
            className="flex-1 truncate text-[11px] text-gray-500"
            title={floorplanName}
          >
            {floorplanName}
          </span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className="text-[10px] text-gray-400">
            {visibleLayerCount}/{layers.length} layers visible
            {dirtyCount > 0 ? (
              <span className="ml-1.5 text-amber-600">· {dirtyCount} unsaved</span>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <button
              type="button"
              onClick={handleClearSelection}
              disabled={!canClearSelection}
              title="Clear focused selection and discard unsaved visibility changes"
              className="rounded-md border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:border-slate-100 disabled:text-slate-300"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={handleSaveVisibility}
              disabled={!canSaveVisibility}
              title={
                dirtyCount === 0
                  ? 'No visibility changes to save'
                  : !floorPlanId
                    ? 'Save requires a backend floor plan id'
                    : 'Save recent layer visibility changes'
              }
              className="rounded-md bg-blue-600 px-2.5 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
            >
              {isSavingVisibility
                ? 'Saving…'
                : dirtyCount > 0
                  ? `Save (${dirtyCount})`
                  : 'Save'}
            </button>
          </div>
        </div>
        {saveNotice && (
          <div className="flex items-start justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 text-[10px] text-slate-600">
            <span className="min-w-0 flex-1 leading-snug">{saveNotice}</span>
            <button
              type="button"
              className="shrink-0 font-semibold text-slate-500 hover:text-slate-700"
              onClick={() => setSaveNotice(null)}
            >
              ×
            </button>
          </div>
        )}
        {!floorPlanId && (
          <div className="text-[10px] leading-snug text-amber-700">
            Local floor plans cannot persist layer visibility until loaded from the backend.
          </div>
        )}
      </div>

      {/* Opacity */}
      <div className="px-3 py-2 border-b border-gray-200 shrink-0">
        <div className="flex items-center justify-between text-[11px] text-gray-500 mb-1">
          <span>Opacity</span>
          <span className="tabular-nums">{Math.round(floorPlanOpacity * 100)}%</span>
        </div>
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={floorPlanOpacity}
          onChange={(e) => dispatch(setFloorPlanOpacity(parseFloat(e.target.value)))}
          className="w-full"
          aria-label="Floor plan opacity"
        />
      </div>

      {/* Search + global controls */}
      <div className="px-3 py-2 border-b border-gray-200 shrink-0 space-y-1.5">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search layers or entity types…"
          className="input-field w-full text-xs"
        />
        <div className="flex gap-1.5">
          <button
            onClick={() => dispatch(setAllDxfLayersVisible(true))}
            className="flex-1 text-[11px] px-2 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
          >
            Show all layers
          </button>
          <button
            onClick={() => dispatch(setAllDxfLayersVisible(false))}
            className="flex-1 text-[11px] px-2 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
          >
            Hide all layers
          </button>
        </div>
      </div>

      {/* Layer list */}
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {filteredLayers.length === 0 && (
          <div className="text-xs text-gray-400 italic px-3 py-3">No layers match.</div>
        )}

        {filteredLayers.map((lvm) => {
          const isExpanded = expandedLayers.has(lvm.layerIndex) || (query.trim() !== '' && lvm.matchingGroups.length > 0);
          const swatch = aciToHex(lvm.color) ?? DEFAULT_STROKE;
          const groupsToShow = query.trim() ? lvm.matchingGroups : lvm.entityGroups;

          return (
            <div
              key={lvm.layerIndex}
              className="border-b border-gray-100 last:border-0"
              onMouseEnter={() => dispatch(setHoveredLayer(lvm.layerIndex))}
              onMouseLeave={() => dispatch(setHoveredLayer(null))}
            >
              {/* Layer header row */}
              <div
                ref={(el) => { layerRowRefs.current[lvm.layerIndex] = el; }}
                className={`flex items-center gap-1.5 px-2 py-1.5 cursor-pointer transition-colors
                  ${lvm.visible ? 'hover:bg-gray-50' : 'opacity-50 hover:bg-gray-50'}
                  ${focusedPolylineIndex != null && polylineLayers[focusedPolylineIndex] === lvm.layerIndex
                    ? 'bg-blue-50 border-l-2 border-blue-400'
                    : ''
                  }`}
                onClick={() => toggleLayerExpand(lvm.layerIndex)}
              >
                {/* Expand arrow */}
                <span className={`text-[10px] text-gray-400 w-3 shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                  ▶
                </span>

                {/* Layer color swatch */}
                <span
                  className="w-2.5 h-2.5 rounded-sm shrink-0 border border-black/10"
                  style={{ background: swatch }}
                />

                {/* Layer name */}
                <span
                  className="flex-1 truncate text-xs text-gray-800 font-medium"
                  title={lvm.name}
                >
                  {lvm.name}
                </span>

                {/* Category badge */}
                {lvm.category && lvm.category !== 'UNCATEGORIZED' && (
                  <span className="text-[9px] px-1 rounded bg-blue-100 text-blue-700 shrink-0">
                    {lvm.category}
                  </span>
                )}

                {/* Entity count */}
                <span className="text-[10px] text-gray-400 tabular-nums shrink-0">
                  {lvm.entityGroups.reduce((s, g) => s + g.indices.length, 0)}
                </span>

                {/* Layer visibility toggle */}
                <button
                  className="w-5 h-5 flex items-center justify-center text-[11px] rounded hover:bg-gray-200 shrink-0"
                  title={lvm.visible ? 'Hide layer' : 'Show layer'}
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch(toggleDxfLayerVisible(lvm.layerIndex));
                  }}
                >
                  <VisibilityIcon visible={lvm.visible} className="w-3.5 h-3.5 text-gray-600" />
                </button>
              </div>

              {/* Entity type groups (shown when layer is expanded) */}
              {isExpanded && (
                <div className="bg-gray-50/60">
                  {/* Per-layer entity bulk controls */}
                  <div className="flex items-center gap-1 px-6 py-1 border-b border-gray-100">
                    <span className="text-[10px] text-gray-400 flex-1">Entities in layer</span>
                    <button
                      className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-gray-200 text-gray-500 hover:bg-gray-100 transition-colors"
                      onClick={() =>
                        dispatch(
                          setAllEntityTypesInLayerVisible({
                            layerIndex: lvm.layerIndex,
                            types: lvm.entityGroups.map((g) => g.type),
                            visible: true,
                          })
                        )
                      }
                    >
                      Show all
                    </button>
                    <button
                      className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-gray-200 text-gray-500 hover:bg-gray-100 transition-colors"
                      onClick={() =>
                        dispatch(
                          setAllEntityTypesInLayerVisible({
                            layerIndex: lvm.layerIndex,
                            types: lvm.entityGroups.map((g) => g.type),
                            visible: false,
                          })
                        )
                      }
                    >
                      Hide all
                    </button>
                  </div>

                  {groupsToShow.map((group) => {
                    const typeKey = `${lvm.layerIndex}:${group.type}`;
                    const isTypeExpanded = expandedTypes.has(typeKey);
                    const isFocusedType =
                      focusedPolylineIndex != null &&
                      polylineLayers[focusedPolylineIndex] === lvm.layerIndex &&
                      (polylineMeta[focusedPolylineIndex]?.type ?? 'UNKNOWN') === group.type;

                    return (
                      <div key={typeKey}>
                        {/* Entity type row */}
                        <div
                          className={`flex items-center gap-1.5 pl-6 pr-2 py-1 cursor-pointer hover:bg-gray-100/80 transition-colors
                            ${isFocusedType ? 'bg-blue-50/80' : ''}
                            ${!group.visible ? 'opacity-50' : ''}`}
                          onClick={() => toggleTypeExpand(typeKey)}
                        >
                          {/* Expand arrow */}
                          <span
                            className={`text-[9px] text-gray-400 w-2.5 shrink-0 transition-transform ${isTypeExpanded ? 'rotate-90' : ''}`}
                          >
                            ▶
                          </span>

                          {/* Entity type icon */}
                          <span className="text-[11px] text-gray-500 w-3 text-center shrink-0">
                            {ENTITY_ICON[group.type] ?? '·'}
                          </span>

                          {/* Type name */}
                          <span className="flex-1 text-[11px] text-gray-700">
                            {group.type}
                          </span>

                          {/* Count */}
                          <span className="text-[10px] text-gray-400 tabular-nums shrink-0">
                            {group.indices.length}
                          </span>

                          {/* Visibility toggle */}
                          <button
                            className="w-5 h-5 flex items-center justify-center text-[10px] rounded hover:bg-gray-200 shrink-0"
                            title={group.visible ? `Hide all ${group.type}` : `Show all ${group.type}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              dispatch(
                                toggleEntityTypeVisible({
                                  layerIndex: lvm.layerIndex,
                                  type: group.type,
                                })
                              );
                            }}
                          >
                            <VisibilityIcon visible={group.visible} className="w-3.5 h-3.5 text-gray-600" />
                          </button>
                        </div>

                        {/* Individual entity rows */}
                        {isTypeExpanded && (
                          <div className="bg-white/70">
                            {group.indices
                              .slice(0, MAX_VISIBLE_ENTITIES)
                              .map((pi) => {
                                const isFocused = pi === focusedPolylineIndex;
                                return (
                                  <div
                                    key={pi}
                                    ref={isFocused ? focusedEntityRef : undefined}
                                    className={`flex items-center gap-1.5 pl-10 pr-2 py-0.5 cursor-pointer text-[10px] transition-colors
                                      ${isFocused
                                        ? 'bg-blue-100 text-blue-800 font-medium'
                                        : 'text-gray-500 hover:bg-gray-100'
                                      }`}
                                    onClick={() => dispatch(setFocusedPolyline(pi))}
                                    title={`Polyline #${pi} · ${group.type} · layer ${lvm.name}`}
                                  >
                                    <span className="text-gray-300 shrink-0">#</span>
                                    <span className="tabular-nums">{pi}</span>
                                    <span className="flex-1 text-gray-300">
                                      {group.type}
                                    </span>
                                    {isFocused && (
                                      <span className="text-[9px] text-blue-500 shrink-0">
                                        ● focused
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            {group.indices.length > MAX_VISIBLE_ENTITIES && (
                              <div className="pl-10 pr-2 py-1 text-[10px] text-gray-400 italic">
                                … {group.indices.length - MAX_VISIBLE_ENTITIES} more{' '}
                                {group.type} entities
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Panel header ─────────────────────────────────────────────────────────────

const PanelHeader: React.FC<{ onClose?: () => void }> = () => (
  <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 shrink-0">
    <span className="font-semibold text-xs text-gray-900">DXF Layers</span>
    <div className="flex-1" />
  </div>
);

export default DxfLayersPanel;
